<template>
  <div>
    <v-icon
      v-if="showCopyIcon && hasCopyAccess"
      @click="handleCopyClick"
      :class="copyButtonClasses"
      size="12"
      color="black"
      title="Copy event to Clipboard"
    >
      fas fa-clipboard
    </v-icon>

    <v-icon
      v-if="showingDocuments"
      class="pull-left mr-3 hyp-note-icon"
      size="12"
      color="black"
      :title="documentNoteTitle"
      @click="handleDocumentNoteClick"
    >
      {{ documentNoteIcon }}
    </v-icon>

    <v-overlay :absolute="true" :value="screenShotting">
      <div class="copy-overlay-indicator">
        <v-icon size="50" color="white">fas fa-check</v-icon>
        <div><span class="application-heading">Copied</span></div>
      </div>
    </v-overlay>

    <ColoredLabelPicker
      v-if="(showingDocuments && showCollaboration && hasCollaborationAccess)"
      class="pull-left mr-3"
      :color="labelColor"
      :colors="labelColors"
      @click="handleLabelColorClick"
    ></ColoredLabelPicker>

    <CollaborationTreePopup
      v-if="showCollaboration && hasCollaborationAccess"
      :article="item"
      :showing-documents="showingDocuments"
      :showing-alerts="showingAlerts"
      :theme="theme"
      class="pull-left folder-icon"
      @click-delete="handleCollabTreePopupDeleteClick"
    ></CollaborationTreePopup>

    <i
      v-if="showReadIcon"
      class="fas fa-book-open hyp-item-read-icon pull-left ml-3"
      :class="{ 'theme--dark white--text': isDarkTheme }"
      :title="readHint"
      @click.stop.prevent="handleReadClick"
    ></i>

    <span v-if="showTranslateIcon" class="hyp-translate-section pull-left ml-3">
      <img
        v-if="!translating"
        :src="languageIcon"
        @click.stop.prevent="handleTranslateClick"
        :class="{
          'hyp-translating-icon': !showingAlerts,
          'hyp-translating-icon-alerts': showingAlerts,
        }"
        :title="translateHint"
      />
      <i
        v-if="translating"
        class="fas fa-circle-notch fa-spin hyp-translating-loader"
        :class="{
          'theme--dark white--text': isDarkTheme,
          'hyp-translating-loader-alerts': showingAlerts,
        }"
      ></i>
    </span>

    <PopupInfo
      v-if="adminInfo"
      :text="adminInfo"
      className="ml-3 adjust-position-1"
      @click="handleAdminInfoClick"
    ></PopupInfo>

    <FeedbackLoop
      v-if="item && !clusterId && showLocationFixer"
      :data="item"
      feedbackType="ARTICLE_GEO_LOCATION"
      :show-thumbs-up="false"
      :show-thumbs-down="false"
      :show-thumbs-down-geo="showLocationFixer"
      :theme="theme"
      class="hyp-feedback-loop pull-left"
      :class="{ 'ml-3': showLocationFixer }"
    ></FeedbackLoop>

    <FeedbackLoop
      v-if="item && clusterId"
      :data="item"
      :event="primaryTopic && primaryTopic[0]"
      feedbackType="CLUSTER_GEO_LOCATION"
      :show-thumbs-up="false"
      :show-thumbs-down="primaryTopic && showThumbsDown"
      :show-thumbs-down-geo="showLocationFixer"
      :show-delete-hotspot="hasMoveClustersAccess"
      :theme="theme"
      class="hyp-feedback-loop pull-left"
      :class="{ 'ml-3': showLocationFixer }"
    ></FeedbackLoop>

    <v-icon
      v-if="showDownloadIcon"
      class="ml-3"
      :class="{ 'theme--dark white--text': isDarkTheme }"
      @click="handleDownloadClick"
      size="12"
      color="black"
      title="Download PDF"
    >
      fa-solid fa-download
    </v-icon>

    <span
      v-if="showPrimaryTopics"
      class="hyp-primary-topic-label pull-left ml-3"
      >{{ topic }}</span
    >

    <div class="clearfix"></div>
  </div>
</template>

<script>
import lodashGet from "lodash/get";

import { mapGetters } from "vuex";

import { MODULE_PREFIX_USER } from "@/store";
import { ICONS } from "@/common/constants/icons";
import { findTopic, findInfluence } from "@/services/events/topics.service";
import {
  hasGroupByName,
  hasCollaborationAccess,
  hasCopyAccess,
} from "@/services/user/user.service";
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  SEARCH_CATEGORIES,
} from "@/common/constants/analytics";
import { getAnalysisReport } from "@/services/reporting/reporting.service";

const EMITTED_ACTIONS = {
  CLICK_COLLAB_DELETE: "click-collab-delete",
  CLICK_READ: "click-read",
  CLICK_TRANSLATE: "click-translate",
  CLICK_ADMIN_INFO: "click-admin-info",
  CLICK_LABEL_COLOR: "click-label-color",
  CLICK_DOCUMENT_NOTE: "click-document-note",
  CLICK_COPY_CONTENT: "click-copy-content",
};

export default {
  name: "DetailsActionIcons",
  components: {
    FeedbackLoop: () => import("@/components/common/FeedbackLoop"),
    PopupInfo: () => import("@/components/common/PopupInfo"),
    ColoredLabelPicker: () => import("@/components/common/ColoredLabelPicker"),
    CollaborationTreePopup: () =>
      import("@/components/collaboration/CollaborationTreePopup"),
  },
  props: {
    item: {
      type: Object,
    },
    showReadIcon: {
      type: Boolean,
    },
    showThumbsDownGeo: {
      type: Boolean,
    },
    showThumbsDown: {
      type: Boolean,
    },
    showPrimaryTopics: {
      type: Boolean,
      default: true,
    },
    primaryTopic: {
      type: Array,
    },
    showingAlerts: {
      type: Boolean,
      default: false,
    },
    showCollaboration: {
      type: Boolean,
      default: true,
    },
    showingDocuments: {
      type: Boolean,
      default: false,
    },
    clusterId: {
      type: String,
    },
    theme: {
      type: String,
    },
    adminInfo: {
      type: String,
    },
    showTranslateIcon: {
      type: Boolean,
    },
    translating: {
      type: Boolean,
    },
    translateHint: {
      type: String,
    },
    readHint: {
      type: String,
    },
    hasDocumentNote: {
      type: Boolean,
      default: false,
    },
    showCopyIcon: {
      type: Boolean,
      default: true,
    },
    showDownloadIcon: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      labelColors: null,
      screenShotting: false,
      hasCollaborationAccess: hasCollaborationAccess(),
      hasCopyAccess: hasCopyAccess(),
    };
  },
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
    }),
    hasMoveClustersAccess() {
      return hasGroupByName("MoveClusters_Access");
    },
    showLocationFixer() {
      return (
        this.showThumbsDownGeo &&
        this.hasMoveClustersAccess &&
        this.item?.properties?.source !== "analysis" &&
        this.item?.properties?.source !== "political" &&
        this.item?.properties?.source !== "maritime"
      );
    },
    isDarkTheme() {
      return this.theme != "light";
    },
    languageIcon() {
      if (this.theme && this.theme === "light") {
        const ICON = ICONS.misc.language.active;
        return require(`@/assets/images${ICON}`);
      } else {
        const ICON = ICONS.misc.language.inactive;
        return require(`@/assets/images${ICON}`);
      }
    },
    topic() {
      if (this.primaryTopic) {
        const PRIMARY_TOPICS = [];
        this.primaryTopic.forEach((topic) => {
          const TOPIC = findTopic(topic);
          if (TOPIC) {
            PRIMARY_TOPICS.push(TOPIC.singular);
          } else {
            const INFLUENCE = findInfluence(topic);
            if (INFLUENCE) {
              PRIMARY_TOPICS.push(INFLUENCE.singular);
            }
          }
        });
        return PRIMARY_TOPICS.join(", ").toUpperCase();
      }
      return null;
    },
    verifiedEvent() {
      return this.item.properties.formatted.type === "verifiedEvent";
    },
    labelColor() {
      if (lodashGet(this.item, "properties.formatted.color")) {
        return this.item.properties.formatted.color;
      }
      return null;
    },
    documentNoteIcon() {
      if (this.hasDocumentNote) {
        return "fas fa-comment";
      }
      return "far fa-comment";
    },
    documentNoteTitle() {
      if (this.hasDocumentNote) {
        return "Note added";
      }
      return "Add note";
    },
    copyButtonClasses() {
      let classes = "pull-left mr-3";
      classes += this.showReadIcon
        ? " hyp-note-icon"
        : " hyp-note-icon-clipboard";
      if (this.isDarkTheme) {
        classes += " theme--dark white--text";
      }
      return classes;
    },
  },
  mounted() {
    if (lodashGet(this.preferences, "collaboration.labels")) {
      this.labelColors = this.preferences.collaboration.labels;
    }
  },
  methods: {
    handleCollabTreePopupDeleteClick() {
      this.$emit(
        EMITTED_ACTIONS.CLICK_COLLAB_DELETE,
        this.item.properties.formatted.documentId
      );
    },
    handleReadClick() {
      this.$emit(
        EMITTED_ACTIONS.CLICK_READ,
        this.item.properties.formatted.documentId
      );
    },
    handleTranslateClick() {
      this.$emit(
        EMITTED_ACTIONS.CLICK_TRANSLATE,
        this.item.properties.formatted
      );
    },
    handleAdminInfoClick() {
      this.$emit(
        EMITTED_ACTIONS.CLICK_ADMIN_INFO,
        this.item.properties.formatted.documentId
      );
      console.info("ClusterId", this.item.properties.formatted.clusterId);
    },
    handleLabelColorClick(color) {
      this.$emit(EMITTED_ACTIONS.CLICK_LABEL_COLOR, {
        documentId: this.item.properties.formatted.documentId,
        color: color,
      });
    },
    handleDocumentNoteClick() {
      this.$emit(
        EMITTED_ACTIONS.CLICK_DOCUMENT_NOTE,
        this.item.properties.formatted.documentId
      );
    },
    async handleCopyClick() {
      this.screenShotting = true;
      setTimeout(() => {
        this.screenShotting = false;
      }, 500);

      this.$emit(
        EMITTED_ACTIONS.CLICK_COPY_CONTENT,
        this.item.properties.formatted.documentId
      );
    },
    async handleDownloadClick() {
      this.handlePageTrackerEvent(
        SEARCH_CATEGORIES.live_search,
        EVENT_ACTIONS.clicked,
        " Analysis Report "
      );
      let requestTimestamp = new Date().toISOString();
      this.isCountryReportLoading = true;
      await getAnalysisReport(this.item.properties, requestTimestamp);
      this.isCountryReportLoading = false;
    },
  },
  watch: {
    "preferences.collaboration.labels": {
      handler() {
        if (lodashGet(this.preferences, "collaboration.labels")) {
          this.labelColors = this.preferences.collaboration.labels;
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.hyp-feedback-loop {
  cursor: pointer;
}
.hyp-translating-icon {
  cursor: pointer;
  height: 11px;
  position: relative;
  top: 3px;
}
.hyp-translating-icon-alerts {
  cursor: pointer;
  height: 11px;
}
.hyp-translating-loader {
  font-size: 11px;
}
.hyp-translating-loader-alerts {
  position: relative;
  top: -4px;
}
.hyp-item-read-icon {
  cursor: pointer;
  font-size: 11px;
  position: relative;
  top: 3px;
}
.hyp-primary-topic-label {
  font-size: 11px;
}
.hyp-note-icon {
  position: relative;
  top: 3px;
}
.hyp-note-icon-clipboard {
  position: relative;
  top: 2px;
  vertical-align: center;
}
.v-overlay {
  left: 0% !important;
  background-color: rgba(0, 0, 0, 0) !important;
}
.v-overlay--active.v-overlay {
  background-color: rgba(0, 0, 0, 0.307) !important;
  width: 100%;
}
.copy-overlay-indicator {
  width: 100px;
  height: 100px;
  background-color: green;
  border-radius: 10px;
  padding: 10px;
  text-align: center;
  font-size: large;
}
.folder-icon {
  position: relative;
  top: 1px;
}
</style>
